import { useState } from "react";
import type { DashboardInstance } from "../mock";
import { SwitcherDropdown } from "../../../components/dashboard/instance-switcher/SwitcherDropdown";
import styles from "./InstanceSwitcher.module.css";

type Props = {
  instances: DashboardInstance[];
  currentInstanceId: string;
};

const REGION_LABELS: Record<NonNullable<DashboardInstance["region"]>, string> = {
  "ap-southeast-2": "Sydney · ap-southeast-2",
  "us-east-1": "Virginia · us-east-1",
  "eu-west-1": "Dublin · eu-west-1",
};

const labelForRegion = (region: DashboardInstance["region"]): string =>
  region === undefined ? "—" : REGION_LABELS[region];

const STATUS_LABELS: Record<DashboardInstance["status"], string> = {
  running: "Running",
  provisioning: "Provisioning",
};

export const InstanceSwitcher = ({
  instances,
  currentInstanceId,
}: Props): JSX.Element => {
  const [selectedId, setSelectedId] = useState<string>(currentInstanceId);
  const current =
    instances.find((i) => i.id === selectedId) ?? instances[0] ?? null;

  return (
    <section className={styles.section} aria-labelledby="dashboard-switcher-title">
      <span className={styles.eyebrow} id="dashboard-switcher-title">
        Instance switcher
      </span>

      <div className={styles.switcherWrap}>
        <SwitcherDropdown
          instances={instances}
          currentId={selectedId}
          onSelect={setSelectedId}
        />
        <span className={styles.hint}>Click to expand &rarr;</span>
      </div>

      {current ? (
        <div className={styles.preview}>
          <span className={styles.previewLabel}>
            <span className={styles.previewLabelDot} aria-hidden /> Now viewing
          </span>
          <h3 className={styles.previewName}>{current.name}</h3>
          <div className={styles.previewMeta}>
            <span className={styles.previewMetaItem}>
              <span className={styles.previewMetaLabel}>Agent</span>
              <span className={styles.previewMetaValue}>
                {current.agentName}
              </span>
            </span>
            <span className={styles.previewMetaItem}>
              <span className={styles.previewMetaLabel}>Region</span>
              <span className={styles.previewMetaValue}>
                {labelForRegion(current.region)}
              </span>
            </span>
            <span className={styles.previewMetaItem}>
              <span className={styles.previewMetaLabel}>Status</span>
              <span className={styles.previewMetaValue}>
                <span
                  className={`${styles.previewStatusDot} ${styles[current.status]}`}
                  aria-hidden
                />{" "}
                {STATUS_LABELS[current.status]}
              </span>
            </span>
            <span className={styles.previewMetaItem}>
              <span className={styles.previewMetaLabel}>Tier</span>
              <span className={styles.previewMetaValue}>{current.tier}</span>
            </span>
          </div>
        </div>
      ) : null}
    </section>
  );
};
